import {Component, Input, OnInit} from '@angular/core';
import {ReviewState} from "../../../../review/models/review-state";
import {FormBuilder, Validators} from "@angular/forms";
import {ReviewService} from "../../../../review/services/review.service";
import {FacadeService} from "../../../../shared/service-facade/facade.service";
import {ValidationDetail} from "../../../../shared/model/ValidationDetail";
import {CustomValidators} from "../../../../shared/validators/custom-validator";
import {ApplicationLogsControllerService} from "leia-api";
import {CoreService} from "../../../../shared/services/core.service";
import {SignupService} from "../../../../signup/service/signup.service";
import {ComponentNameEnum} from "../../../../shared/model/component-name.enum";
import { INVITATION_LOG_KEY } from 'src/app/shared/constants';
import { UtilitiesService } from 'src/app/shared/services/utilities.service';

@Component({
  selector: 'app-personal-details',
  templateUrl: './personal-details.component.html',
  styleUrls: ['./personal-details.component.scss']
})
export class PersonalDetailsComponent implements OnInit {
  @Input() reviewState: ReviewState = ReviewState.Initial;
  reviewStates = ReviewState;
  uneditedFormData: any;
  isInReadOnlyState = false;
  validationDetail: ValidationDetail;
  account = "";
  maxDate = new Date();
  minDate = new Date();

  invitationForm = this.formBuilder.group({
    firstName: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(40), CustomValidators.applicantNameSpecialCharacterValidator]],
    lastName: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(40), CustomValidators.applicantNameSpecialCharacterValidator]],
    birthDate: ['', Validators.required],
    emailAddress: ['', [Validators.email, Validators.minLength(3), Validators.maxLength(100), Validators.required, CustomValidators.emailAddressValidator]]
  }, { updateOn: "blur" });

  private _formHasDuplicatedData = false;
  //the above can be used to check if the persons name is the same as the reciepient.

  constructor(
    private reviewService: ReviewService,
    private facadeService: FacadeService,
    private formBuilder: FormBuilder,
    private signupService: SignupService,
  ) {
    this.validationDetail = new ValidationDetail(this.invitationForm, new Map<string, string>());
  }

  ngOnInit(): void {
    this._setupValidationMap();
    this._setupStatusTracker();

    if(this.reviewState === ReviewState.ReadOnly)
      this._loadApplicationData();

    const accountInfo = this.facadeService.getNewAccountInformation();
    this.account = `${accountInfo.accountType}...${accountInfo.accountNumber.slice(accountInfo.accountNumber.length - 4)}`;

    this.signupService.addFormToTrackState(this.invitationForm);
  }

  private _setupStatusTracker(): void {
    this.invitationForm.statusChanges.subscribe(validChange => {
      this._updateValidations();
      this._updateApplication();
    });
  }

  private _updateApplication() {
    const {firstName, lastName, birthDate, emailAddress} = this.invitationForm.value;

    const invitationLog = {
      firstName: firstName,
      lastName: lastName,
      birthDate: UtilitiesService.getDateString(birthDate),
      emailAddress: emailAddress
    }

    this.facadeService.addStorageItem(INVITATION_LOG_KEY, JSON.stringify(invitationLog));
  }

  private _updateValidations(): void {
    this.signupService.validationsChange.emit(this.validationDetail);
  }


  getFormControlValue(name: string): any {
    return this.invitationForm.get(name)?.value;
  }

  editDetails() {
    this.uneditedFormData = this.invitationForm.value;
    this.reviewState = ReviewState.Edit;

    this.reviewService.editStateArray.push([ComponentNameEnum.PersonalDetails.toString(), 0]);
    this.reviewService.editStateMapChanged.emit();
  }

  cancel() {
    this.invitationForm.patchValue(this.uneditedFormData, {emitEvent: false});
    this.reviewState = ReviewState.ReadOnly;
    this._formHasDuplicatedData = false;

    this.reviewService.editStateArray.splice(this.reviewService.editStateArray.findIndex(val => val[0] === ComponentNameEnum.PersonalDetails.toString() && val[1] === 0, 1));
    this.reviewService.editStateMapChanged.emit();
  }

  save() {
    if (!this.invitationForm.valid || this._formHasDuplicatedData) return;

    this._updateApplication();
    this.reviewState = ReviewState.ReadOnly;

    this.reviewService.editStateArray.splice(this.reviewService.editStateArray.findIndex(val => val[0] === ComponentNameEnum.PersonalDetails.toString() && val[1] === 0), 1);
    this.reviewService.editStateMapChanged.emit();
  }

  private _setupValidationMap(): void {
    let labelMap = new Map<string, string>();
    labelMap.set("firstName", "First Name");
    labelMap.set("lastName", "Last Name");
    labelMap.set("birthDate", "Date of birth");
    labelMap.set("emailAddress", "Personal Email address");

    this.validationDetail = new ValidationDetail(this.invitationForm, labelMap);
    let nowDate = new Date();

    this.maxDate = new Date(nowDate.getFullYear() - 18, nowDate.getMonth(), nowDate.getDate());
    this.minDate = new Date(1900, 1, 1);

  }

  getFieldError(key: string): any {
    if(this.invitationForm.get(key)?.hasError('required')){
      return this.validationDetail.labels.get(key) + " is required.";
    }

    return this.validationDetail.labels.get(key) + " is invalid.";
  }

  private _loadApplicationData() {
    const {firstName, lastName, birthDate, emailAddress} = JSON.parse(this.facadeService.getStorageItem(INVITATION_LOG_KEY));

    this.invitationForm.patchValue({firstName, lastName,  birthDate, emailAddress});
    this.invitationForm.updateValueAndValidity();
  }

  onDateKeyUp(event: KeyboardEvent) {
    let element = event.currentTarget as HTMLInputElement
    const value = element.value;
    if (event.keyCode != 8) { //skip backspace event
      if (value.length == 2) {
        element.value = value + '/';
      } else if (value.length == 5) {
        element.value = value + '/';
      }
    }
  }
}
